import { alpha, Alert, Box, Button, CircularProgress, IconButton, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';

// Shared shell for the Generate Bracket dialog: a tinted header with the event
// name and step rail, a scrollable body and a sticky footer with the actions.
export const BracketWizard = ({
  icon,
  eventName,
  steps,
  activeStep,
  onStepClick,
  children,
  error,
  generating,
  canContinue,
  canGenerate,
  onCancel,
  onBack,
  onNext,
  onGenerate
}) => {
  const theme = useTheme();
  const isLast = activeStep === steps.length - 1;
  const headerBackground =
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.yellow.main, 0.14)} 0%, ${alpha(
          theme.palette.background.paper,
          0
        )} 62%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.yellow.main, 0.32)} 0%, ${alpha(
          theme.palette.background.paper,
          0
        )} 64%)`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: { xs: 'calc(100vh - 80px)', sm: 'calc(100vh - 96px)' },
        bgcolor: 'background.paper'
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 2.5 },
          pb: 2,
          backgroundImage: headerBackground,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 46,
              height: 46,
              flexShrink: 0,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'yellow.main',
              color: '#18181b',
              boxShadow: `0 8px 22px ${alpha(theme.palette.yellow.main, 0.35)}`
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.3, lineHeight: 1.2 }}>
              Generate bracket
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {eventName}
            </Typography>
          </Box>
          <IconButton onClick={onCancel} disabled={generating} aria-label="Close generate bracket">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack direction="row" alignItems="center" sx={{ mt: 2.5 }}>
          {steps.map((label, index) => {
            const done = index < activeStep;
            const current = index === activeStep;

            return (
              <Stack
                key={label}
                direction="row"
                alignItems="center"
                sx={{ flex: index === steps.length - 1 ? '0 0 auto' : 1, minWidth: 0 }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  onClick={done && onStepClick ? () => onStepClick(index) : undefined}
                  sx={{ cursor: done ? 'pointer' : 'default', minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 13,
                      fontWeight: 700,
                      bgcolor: current ? 'primary.main' : done ? 'success.main' : 'action.hover',
                      color: current ? 'primary.contrastText' : done ? 'common.white' : 'text.secondary',
                      transition: 'background-color .2s ease, color .2s ease'
                    }}
                  >
                    {done ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      fontWeight: current ? 700 : 500,
                      color: current ? 'text.primary' : 'text.secondary',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {label}
                  </Typography>
                </Stack>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 12,
                      height: 2,
                      mx: { xs: 1, sm: 1.5 },
                      borderRadius: 1,
                      bgcolor: done ? 'success.main' : 'divider'
                    }}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 }, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {children}
      </Box>

      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.75,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5
        }}
      >
        <Button onClick={onCancel} disabled={generating} color="inherit">
          Cancel
        </Button>
        <Stack direction="row" spacing={1}>
          {activeStep > 0 && (
            <Button onClick={onBack} disabled={generating} variant="outlined">
              Back
            </Button>
          )}
          {isLast ? (
            <Button
              variant="contained"
              onClick={onGenerate}
              disabled={!canGenerate || generating}
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                bgcolor: 'yellow.main',
                color: '#18181b',
                '&:hover': { bgcolor: alpha(theme.palette.yellow.main, 0.85) },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'text.disabled' }
              }}
            >
              {generating ? 'Generating…' : 'Generate bracket'}
            </Button>
          ) : (
            <Button variant="contained" onClick={onNext} disabled={!canContinue}>
              Continue
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

// Small decorative bracket used on the size cards.
export const BracketGlyph = ({ sx }) => (
  <Box
    component="svg"
    viewBox="0 0 64 36"
    aria-hidden="true"
    sx={{ display: 'block', width: '100%', maxWidth: 120, height: 36, ...sx }}
  >
    <g stroke="currentColor" strokeWidth="1.4" fill="none" opacity="0.45" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4.5H18V9H24" />
      <path d="M12 13.5H18" />
      <path d="M12 22.5H18V27H24" />
      <path d="M12 31.5H18" />
      <path d="M34 9H40V18H46" />
      <path d="M34 27H40V18" />
    </g>
    <g fill="currentColor">
      <rect x="2" y="2" width="10" height="5" rx="2" />
      <rect x="2" y="11" width="10" height="5" rx="2" />
      <rect x="2" y="20" width="10" height="5" rx="2" />
      <rect x="2" y="29" width="10" height="5" rx="2" />
      <rect x="24" y="6.5" width="10" height="5" rx="2" />
      <rect x="24" y="24.5" width="10" height="5" rx="2" />
      <rect x="46" y="15.5" width="10" height="5" rx="2" />
    </g>
  </Box>
);

export const BracketTypeCard = ({ selected, disabled, label, meta, roundsLabel, onSelect, children }) => {
  const theme = useTheme();

  const handleKeyDown = (event) => {
    if (disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-pressed={selected}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={handleKeyDown}
      sx={{
        position: 'relative',
        p: 1.75,
        borderRadius: 3,
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
        boxShadow: selected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        transition: 'transform .16s ease, box-shadow .16s ease, border-color .16s ease',
        '&:hover': disabled
          ? {}
          : {
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[4]
            },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2
        }
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {meta}
          </Typography>
        </Box>
        {selected && <CheckCircleIcon color="success" fontSize="small" />}
      </Stack>
      <BracketGlyph
        sx={{
          my: 1.25,
          color: selected ? theme.palette.primary.main : theme.palette.text.disabled
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {roundsLabel}
      </Typography>
      {children}
    </Box>
  );
};

// Compact entity picker used on pairing rows. Options are { value, label } and
// the empty value renders the placeholder in muted text.
export const MatchSelect = ({ value, onChange, placeholder, options, disabled, sx }) => (
  <Select
    size="small"
    displayEmpty
    value={value || ''}
    onChange={(event) => onChange(event.target.value)}
    disabled={disabled}
    sx={{
      flex: 1,
      minWidth: 0,
      bgcolor: 'background.paper',
      '& .MuiSelect-select': { py: 0.9, fontSize: '0.875rem' },
      ...sx
    }}
    renderValue={(selected) => {
      if (!selected) {
        return (
          <Typography component="span" variant="body2" color="text.disabled">
            {placeholder}
          </Typography>
        );
      }

      const option = options.find((entry) => entry.value === selected);

      return (
        <Typography component="span" variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {option ? option.label : String(selected)}
        </Typography>
      );
    }}
  >
    <MenuItem value="">
      <Typography variant="body2" color="text.disabled">
        {placeholder}
      </Typography>
    </MenuItem>
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
        {option.label}
      </MenuItem>
    ))}
  </Select>
);

export const WizardHint = ({ children }) => (
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
    {children}
  </Typography>
);
